import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { useMyTasks, type MyTask, type InternalProject, type ReiseProject } from "@/hooks/use-my-tasks";
import {
  useInternalProjectTasks,
  internalTaskToMyTask,
  type InternalProjectTask,
} from "@/hooks/use-internal-project-tasks";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import TimeTrackingPopup from "./TimeTrackingPopup";
import { SrIcon } from "@/components/ui/SrIcon";
import { InternalTaskPickerGroup } from "./InternalTaskPickerGroup";
import { TaskGroupSection } from "./TaskGroupSection";
import { ReiseGroupSection } from "./ReiseGroupSection";

/** VIB-909: shape a reisetid-eligible project into a MyTask. budget_line_id stays
 * empty — it is resolved via get_or_create_reisetid_line at save time. */
function reiseToTask(p: ReiseProject): MyTask {
  return {
    id: `reise:${p.id}`,
    budget_line_id: "",
    project_id: p.id,
    task_description: "Reisetid (medgått)",
    task_status: "in_progress",
    phase: "reise",
    project_name: p.project_code ? `${p.project_code} — ${p.name}` : p.name,
    assigned_hours: 0,
    logged_hours: 0,
    is_reisetid: true,
  };
}

const PHASE_LABELS: Record<string, string> = {
  preprod: "Preproduksjon",
  production: "Produksjon",
  postprod: "Postproduksjon",
  reise: "Reise",
  intern: "Intern",
};

/**
 * "Ny registrering" popup — first pick a task, then log time.
 * Keyboard: ArrowDown/ArrowUp to navigate, Enter to select, Esc to close.
 */
export default function NewRegistrationPopup({
  onClose,
  anchorRef,
  initialDate,
}: {
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  initialDate?: string;
}) {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const { data } = useMyTasks(userId);
  const tasks = useMemo(() => data?.tasks ?? [], [data?.tasks]);
  const internalProjects = useMemo(() => data?.internalProjects ?? [], [data?.internalProjects]);
  const reiseProjects = useMemo(() => data?.reiseProjects ?? [], [data?.reiseProjects]);

  // VIB-1208: internprosjekter velges på oppgavenivå (budget_lines) i stedet
  // for prosjektnivå.
  const { data: internalTasksData } = useInternalProjectTasks();
  const internalTasks = useMemo(() => internalTasksData ?? [], [internalTasksData]);

  const [selectedTask, setSelectedTask] = useState<MyTask | null>(null);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const popupRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [topPx, setTopPx] = useState(0);

  // Position vertically below anchor, horizontally centered via CSS
  const updateTop = useCallback(() => {
    if (!anchorRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    setTopPx(anchor.bottom + 8);
  }, [anchorRef]);

  useEffect(() => {
    updateTop();
    requestAnimationFrame(updateTop);
    window.addEventListener("scroll", updateTop, true);
    window.addEventListener("resize", updateTop);
    return () => {
      window.removeEventListener("scroll", updateTop, true);
      window.removeEventListener("resize", updateTop);
    };
  }, [updateTop]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  // Filter active tasks (not completed/approved/rejected)
  const activeTasks = useMemo(() => {
    return tasks.filter((t) =>
      t.task_status === "in_progress" || t.task_status === "not_started"
    );
  }, [tasks]);

  // Search filter
  const filtered = useMemo(() => {
    if (!search.trim()) return activeTasks;
    const q = search.toLowerCase();
    return activeTasks.filter(
      (t) =>
        t.project_name.toLowerCase().includes(q) ||
        t.task_description.toLowerCase().includes(q) ||
        (PHASE_LABELS[t.phase] ?? t.phase ?? "").toLowerCase().includes(q)
    );
  }, [activeTasks, search]);

  // VIB-1208: internprosjekter gruppert med sine oppgaver.
  // Prosjekter uten treff på verken eget navn eller noen oppgave filtreres
  // bort; prosjekter uten oppgaver ennå beholdes (kan opprette "+ Ny oppgave").
  const internalGroups = useMemo(() => {
    const tasksByProject = new Map<string, InternalProjectTask[]>();
    for (const t of internalTasks) {
      const list = tasksByProject.get(t.project_id) ?? [];
      list.push(t);
      tasksByProject.set(t.project_id, list);
    }
    const q = search.trim().toLowerCase();
    return internalProjects
      .map((project) => {
        const projectTasks = tasksByProject.get(project.id) ?? [];
        if (!q) return { project, tasks: projectTasks };
        const projectMatches = project.name.toLowerCase().includes(q);
        const matchingTasks = projectTasks.filter((t) => t.description.toLowerCase().includes(q));
        if (projectMatches) return { project, tasks: projectTasks };
        if (matchingTasks.length > 0) return { project, tasks: matchingTasks };
        return null;
      })
      .filter((g): g is { project: InternalProject; tasks: InternalProjectTask[] } => g !== null);
  }, [internalTasks, internalProjects, search]);

  // Filtered reisetid-eligible projects (VIB-909)
  const filteredReise = useMemo(() => {
    if (!search.trim()) return reiseProjects;
    const q = search.toLowerCase();
    return reiseProjects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.project_code ?? "").toLowerCase().includes(q) ||
        "reisetid".includes(q)
    );
  }, [reiseProjects, search]);

  // Group by project
  const grouped = useMemo(() => {
    const map = new Map<string, MyTask[]>();
    for (const t of filtered) {
      const list = map.get(t.project_name) ?? [];
      list.push(t);
      map.set(t.project_name, list);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // Flat list of selectable items for keyboard navigation
  const flatItems = useMemo(() => {
    const items: Array<
      | { type: "task"; task: MyTask }
      | { type: "internal-task"; task: InternalProjectTask }
      | { type: "reise"; project: ReiseProject }
    > = [];
    for (const [, projectTasks] of grouped) {
      for (const t of projectTasks) items.push({ type: "task", task: t });
    }
    for (const g of internalGroups) {
      for (const t of g.tasks) items.push({ type: "internal-task", task: t });
    }
    for (const p of filteredReise) items.push({ type: "reise", project: p });
    return items;
  }, [grouped, internalGroups, filteredReise]);

  // Reset active index when search changes
  useEffect(() => {
    setActiveIdx(0);
  }, [search]);

  // Clamp active index to valid range
  useEffect(() => {
    if (activeIdx >= flatItems.length && flatItems.length > 0) {
      setActiveIdx(flatItems.length - 1);
    }
  }, [activeIdx, flatItems.length]);

  const selectItem = useCallback(
    (idx: number) => {
      const item = flatItems[idx];
      if (!item) return;
      if (item.type === "task") setSelectedTask(item.task);
      else if (item.type === "internal-task") setSelectedTask(internalTaskToMyTask(item.task));
      else setSelectedTask(reiseToTask(item.project));
    },
    [flatItems]
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  // Keyboard: navigation + close
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, flatItems.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectItem(activeIdx);
    }
  };

  // Once a task is selected, delegate to TimeTrackingPopup
  if (selectedTask) {
    return (
      <TimeTrackingPopup
        task={selectedTask}
        onClose={onClose}
        userId={userId}
        // VIB-1967: uten denne ble føringen lagret med assignment_id = null, og
        // forsvant fra brukerens egen oppgaveliste (98,98 t på 19 prosjekter).
        // MÅ være `assignment_id`, ikke `.id` — interne oppgaver og reisetid
        // legger prosjekt-/linje-id og "reise:<id>" i `.id`.
        assignmentId={selectedTask.assignment_id ?? null}
        anchorRef={anchorRef}
        initialDate={initialDate}
      />
    );
  }

  let itemIdx = -1;

  return createPortal(
    <div
      ref={popupRef}
      className="fixed rounded-xl border bg-popover shadow-lg z-50 w-[calc(100%-16px)] xl:w-[460px] left-1/2 -translate-x-1/2"
      style={{ top: topPx }}
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="border-b px-3 xl:px-4 py-3 flex justify-between items-center">
        <p className="text-sm font-medium">Ny registrering</p>
        <button onClick={onClose} className="rounded-sm hover:bg-muted p-1" title="Lukk">
          <SrIcon name="x" className="w-4 h-4 text-muted-foreground"  />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 xl:px-4 py-2 border-b">
        <div className="relative">
          <SrIcon name="search" className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"  />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk oppgave eller prosjekt..."
            className="h-8 text-xs pl-7"
            autoFocus
          />
        </div>
      </div>

      {/* Task list */}
      <div ref={listRef} className="max-h-[360px] overflow-y-auto">
        {flatItems.length === 0 && (
          <p className="text-xs text-muted-foreground px-4 py-6 text-center">
            {search ? "Ingen treff" : "Ingen aktive oppgaver"}
          </p>
        )}
        {grouped.map(([projectName, projectTasks]) => {
          const startIdx = itemIdx + 1;
          itemIdx += projectTasks.length;
          return (
            <TaskGroupSection
              key={projectName}
              projectName={projectName}
              tasks={projectTasks}
              activeIdx={activeIdx}
              startIdx={startIdx}
              onSelectTask={setSelectedTask}
              onHoverIdx={setActiveIdx}
            />
          );
        })}
        {internalGroups.map((g) => {
          const startIdx = itemIdx + 1;
          itemIdx += g.tasks.length;
          return (
            <InternalTaskPickerGroup
              key={g.project.id}
              project={g.project}
              tasks={g.tasks}
              activeIdx={activeIdx}
              startIdx={startIdx}
              onSelectTask={(t) => setSelectedTask(internalTaskToMyTask(t))}
              onHoverIdx={setActiveIdx}
            />
          );
        })}
        {(() => {
          const startIdx = itemIdx + 1;
          itemIdx += filteredReise.length;
          return (
            <ReiseGroupSection
              projects={filteredReise}
              activeIdx={activeIdx}
              startIdx={startIdx}
              onSelectProject={(p) => setSelectedTask(reiseToTask(p))}
              onHoverIdx={setActiveIdx}
            />
          );
        })()}
      </div>

      {/* Footer hint */}
      <div className="px-3 xl:px-4 py-2 border-t text-[10px] text-muted-foreground">
        <span className="mr-3">↑↓ navigering</span>
        <span className="mr-3">↵ velg</span>
        <span>Esc lukk</span>
      </div>
    </div>,
    document.body
  );
}
